import React, { useState } from "react";
import { Settings } from "lucide-react";
import { cn } from "../../lib/utils";
import Card from "../ui/Card";

const DisplaySettings = ({ previewSettings, onSettingsChange }) => {
  const [isTimeDisplayOpen, setIsTimeDisplayOpen] = useState(false);
  const [isTimeAgoOpen, setIsTimeAgoOpen] = useState(false);

  const handleChange = (key, value) => {
    onSettingsChange({ [key]: value });
  };

  const timeDisplayOptions = [
    { value: "date", label: "Date" },
    { value: "datetime", label: "Datetime" },
    { value: "hidden", label: "Hidden" },
  ];

  const timeAgoOptions = [
    { value: "show", label: "Show" },
    { value: "hide", label: "Hide" },
  ];

  const getTimeDisplayLabel = (value) => {
    const option = timeDisplayOptions.find((opt) => opt.value === value);
    return option ? option.label : "Datetime";
  };

  const getTimeAgoLabel = (value) => {
    const option = timeAgoOptions.find((opt) => opt.value === value);
    return option ? option.label : "Show";
  };

  return (
    <Card className="p-6 space-y-6">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Settings className="w-5 h-5" />
        Display Settings
      </h3>

      {/* Time Display */}
      <div className="relative">
        <label className="text-sm font-medium text-zinc-300 mb-2 block">
          Time Display
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsTimeDisplayOpen(!isTimeDisplayOpen)}
            className="w-full px-3 py-2 rounded-md border border-zinc-700 bg-zinc-900 text-white text-sm flex items-center justify-between hover:bg-zinc-800 transition-colors"
          >
            <span>{getTimeDisplayLabel(previewSettings.timeDisplay || "datetime")}</span>
            <svg
              className={cn(
                "w-4 h-4 text-zinc-400 transition-transform",
                isTimeDisplayOpen && "rotate-180"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isTimeDisplayOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsTimeDisplayOpen(false)}
              ></div>
              <div className="absolute z-20 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg">
                {timeDisplayOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      handleChange("timeDisplay", option.value);
                      setIsTimeDisplayOpen(false);
                    }}
                    className={cn(
                      "w-full px-3 py-2 text-left text-sm text-white hover:bg-zinc-800 transition-colors flex items-center justify-between",
                      option.value === (previewSettings.timeDisplay || "datetime") &&
                        "bg-zinc-800"
                    )}
                  >
                    <span>{option.label}</span>
                    {option.value === (previewSettings.timeDisplay || "datetime") && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Show Metrics */}
      <div>
        <label className="text-sm font-medium text-zinc-300 mb-2 block">
          Show Metrics
        </label>
        <button
          onClick={() =>
            handleChange("showMetrics", !previewSettings.showMetrics)
          }
          className={cn(
            "w-full px-4 py-2 rounded-lg text-sm font-medium transition-all",
            previewSettings.showMetrics
              ? "bg-green-600 text-white"
              : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
          )}
        >
          {previewSettings.showMetrics ? "Visible" : "Hidden"}
        </button>
      </div>

      {/* Time Ago Display */}
      <div className="relative">
        <label className="text-sm font-medium text-zinc-300 mb-2 block">
          Time Ago Display
        </label>
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsTimeAgoOpen(!isTimeAgoOpen)}
            className="w-full px-3 py-2 rounded-md border border-zinc-700 bg-zinc-900 text-white text-sm flex items-center justify-between hover:bg-zinc-800 transition-colors"
          >
            <span>{getTimeAgoLabel(previewSettings.timeAgoDisplay || "show")}</span>
            <svg
              className={cn(
                "w-4 h-4 text-zinc-400 transition-transform",
                isTimeAgoOpen && "rotate-180"
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {isTimeAgoOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsTimeAgoOpen(false)}
              ></div>
              <div className="absolute z-20 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg">
                {timeAgoOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      handleChange("timeAgoDisplay", option.value);
                      setIsTimeAgoOpen(false);
                    }}
                    className={cn(
                      "w-full px-3 py-2 text-left text-sm text-white hover:bg-zinc-800 transition-colors flex items-center justify-between",
                      option.value === (previewSettings.timeAgoDisplay || "show") &&
                        "bg-zinc-800"
                    )}
                  >
                    <span>{option.label}</span>
                    {option.value === (previewSettings.timeAgoDisplay || "show") && (
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default DisplaySettings;

